import {
  type ChangeEvent,
  cloneElement,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { type BigDecimalRangeDto } from '@ev-tech/search-modules-api';

import { type RangeInputProps } from './RangeInput.types';
import { Headline } from '../Headline/Headline';
import { validateRangeValues } from '../../utils/numberFormatters';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useAppContext } from '../../hooks/useAppContext';

import styles from './RangeInput.module.css';

type RangeInputElementProps = {
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  'data-testid'?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export const RangeInput = ({
  defaultRange,
  onChange,
  label,
  id,
  hasLabelHidden = false,
  inputComponent,
  'data-testid': dataTestId,
}: RangeInputProps & {
  label: string;
  id: string;
  inputComponent: ReactElement<RangeInputElementProps>;
  'data-testid'?: string;
}) => {
  const { t } = useAppContext();
  const { isMobile } = useBreakpoint();
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);

  const [range, setRange] = useState<BigDecimalRangeDto | undefined>(defaultRange ?? undefined);

  const handleInputFocus = useCallback(() => {
    if (!isMobile || hasLabelHidden) return;
    // Reason: iOS scrolls focused inputs into view and can push the fieldset legend off-screen.
    setTimeout(() => {
      fieldsetRef.current?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      });
    }, 100);
  }, [hasLabelHidden, isMobile]);

  useEffect(() => {
    if (!defaultRange) {
      setRange(undefined);
    }
  }, [defaultRange]);

  const handleOnChange = (range: BigDecimalRangeDto) => {
    setRange({ ...range });
    onChange(validateRangeValues({ ...range }));
  };

  const minInput = cloneElement(inputComponent, {
    placeholder: t('search.searchbar.minLabel'),
    id: `${id}Min`,
    name: `${id}Min`,
    'aria-label': `${label} ${t('search.searchbar.minLabel')}`,
    value: range ? range.min?.toString() : '',
    onChange: ({ target }: ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseInt(target.value);
      handleOnChange({
        ...range,
        min: Number.isNaN(value) || value === 0 ? undefined : value,
      });
    },
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      inputComponent.props.onFocus?.(event);
      handleInputFocus();
    },
    'data-testid': `${dataTestId}_input-min`,
  });
  const maxInput = cloneElement(inputComponent, {
    placeholder: t('search.searchbar.maxLabel'),
    id: `${id}Max`,
    name: `${id}Max`,
    'aria-label': `${label} ${t('search.searchbar.maxLabel')}`,
    value: range ? range.max?.toString() : '',
    onChange: ({ target }: ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseInt(target.value);
      handleOnChange({
        ...range,
        max: Number.isNaN(value) || value === 0 ? undefined : value,
      });
    },
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      inputComponent.props.onFocus?.(event);
      handleInputFocus();
    },
    'data-testid': `${dataTestId}_input-max`,
  });
  return (
    <fieldset ref={fieldsetRef} className={styles.fieldset} data-testid={dataTestId}>
      {hasLabelHidden ? (
        <legend className={styles.visuallyHidden}>{label}</legend>
      ) : (
        <Headline className={styles.legend} as="legend" size="h6" label={label} />
      )}
      <div className={styles.fieldsetContainer}>
        {minInput}
        <Copy className={styles.fieldsSeparator} size="l" as="span" text="–" />
        {maxInput}
      </div>
    </fieldset>
  );
};
